import { call, put, race, select, take, takeEvery } from 'redux-saga/effects';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { resourceSagaUtils, resourceStoreTypes } from '../core';
import { preferencesV2Actions } from '../preferences-v2';

import * as actions from './institutionsActions';
import {
  transformResponseToInstitution,
  transformResponseToInstitutions,
  transformSearchResponseToInstitutions,
} from './institutionsTransformers';
import type { Institution } from './institutionsTypes';

const mkQcsSyncInstitutionsConfig = resourceStoreTypes.createTypedQcsSyncResourcesConfigFactory<Institution>();

function* searchForInstitutions(action: ReturnType<typeof actions.searchForInstitutions>): Generator<any, void, any> {
  const { searchString } = action.payload;

  const institutionSearchTrimDot = yield select(featureFlagsSelectors.getFeatureFlag, 'institutionSearchTrimDot') ??
    true;

  const resourceConfig = mkQcsSyncInstitutionsConfig({
    resourceBaseUrl: '/institutions/search',
    // @ts-expect-error institutions/search has a legacy response format
    transformResponseToResources: transformSearchResponseToInstitutions,
    axiosConfig: { params: { name: institutionSearchTrimDot ? searchString.replace(/\./g, '') : searchString } },
    successAction: actions.searchForInstitutionsSuccess,
    failureAction: actions.searchForInstitutionsFailure,
  });

  yield call(resourceSagaUtils.qcsSyncGetResources<Institution>, resourceConfig, action);
}

function* getInstitution(action: ReturnType<typeof actions.getInstitution>) {
  yield put(preferencesV2Actions.getPreferencesV2());
  yield race([take(preferencesV2Actions.getPreferencesV2Success), take(preferencesV2Actions.getPreferencesV2Failure)]);

  const resourceConfig = mkQcsSyncInstitutionsConfig({
    resourceName: 'institutions',
    resourceBaseUrl: (a) => `/institutions/${a.payload.id}`,
    getLastSyncDate: null,
    transformResponseToResource: transformResponseToInstitution,
    successAction: actions.getInstitutionResponse,
    failureAction: actions.getInstitutionResponse,
  });

  yield call(resourceSagaUtils.qcsSyncGetResource<Institution>, resourceConfig, action);
}

// =============================================================================
// Watchers
// =============================================================================

function* getInstitutionActionWatcher() {
  yield takeEvery(actions.getInstitution, getInstitution);
}

function* getPopularInstitutionsActionWatcher() {
  const resourceConfig = mkQcsSyncInstitutionsConfig({
    resourceName: 'popular-institutions',
    resourceBaseUrl: '/institutions/suggested',
    axiosConfig: {
      params: {
        limit: 15,
      },
    },
    getLastSyncDate: null,
    // @ts-expect-error institutions/suggested has a legacy response form
    transformResponseToResources: transformResponseToInstitutions,
    successAction: actions.getPopularInstitutionsSuccess,
    failureAction: actions.getPopularInstitutionsFailure,
  });
  yield takeEvery(
    actions.getPopularInstitutions.type,
    resourceSagaUtils.qcsSyncGetResources<Institution>,
    resourceConfig,
  );
}

function* searchForInstitutionsActionWatcher() {
  yield takeEvery(actions.searchForInstitutions, searchForInstitutions);
}

// =============================================================================
// Exports
// =============================================================================

export default [getInstitutionActionWatcher, getPopularInstitutionsActionWatcher, searchForInstitutionsActionWatcher];
